import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { ThemeService } from '../../services/theme.service';
import { TreeComponent, TreeNode, TreeState } from '../tree/tree';
import { RelayDashboardComponent } from '../relay-dashboard/relay-dashboard';

const SIDEBAR_WIDTH_KEY = 'sidebar_width_percent';
const DEFAULT_SIDEBAR_PERCENT = 25;
const MIN_SIDEBAR_PERCENT = 15;
const MAX_SIDEBAR_PERCENT = 70;
const MOBILE_BREAKPOINT = 900;

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, TreeComponent, RelayDashboardComponent],
  templateUrl: './home.html',
  styleUrl: './home.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent implements OnInit {
  selectedNode: TreeNode | null = null;

  sidebarWidthPercent = DEFAULT_SIDEBAR_PERCENT;
  minSidebarPercent = MIN_SIDEBAR_PERCENT;
  maxSidebarPercent = MAX_SIDEBAR_PERCENT;

  isResizing = false;

  isSidebarOpen = false;
  isDesktop = true;

  treeState: TreeState = { count: 0, operatorOnly: false };

  private containerLeft = 0;
  private containerWidth = 0;

  private lastUserId: string | null = null;

  constructor(
    public authService: AuthService,
    public themeService: ThemeService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {
    effect(() => {
      const user = this.authService.currentUser();
      const userId = user?.sub ?? user?.username ?? null;

      if (this.lastUserId !== null && this.lastUserId !== userId) {
        this.selectedNode = null;
        this.treeState = { count: 0, operatorOnly: false };
        this.cdr.markForCheck();
      }

      this.lastUserId = userId;
    });

    effect(() => {
      this.themeService.theme$;
      this.cdr.markForCheck();
    });
  }

  get isNoAccess(): boolean {
    return this.treeState.operatorOnly && this.treeState.count === 0;
  }

  get isSingleNodeMode(): boolean {
    return this.treeState.operatorOnly && this.treeState.count === 1;
  }

  get showSidebar(): boolean {
    return !this.isNoAccess && !this.isSingleNodeMode;
  }

  ngOnInit(): void {
    this.restoreSidebarWidth();
    this.updateViewportMode();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewportMode();

    if (this.sidebarWidthPercent < this.minSidebarPercent) {
      this.sidebarWidthPercent = this.minSidebarPercent;
    } else if (this.sidebarWidthPercent > this.maxSidebarPercent) {
      this.sidebarWidthPercent = this.maxSidebarPercent;
    }
  }

  @HostListener('window:keydown.escape')
  onEscape(): void {
    this.closeSidebar();
  }

  @HostListener('window:blur')
  onWindowBlur(): void {
    if (!this.isResizing) return;

    this.isResizing = false;
    this.persistSidebarWidth();

    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  onNodeSelected(node: TreeNode): void {
    this.selectedNode = node;

    if (!this.isDesktop) {
      this.isSidebarOpen = false;
      this.lockBodyScroll(false);
    }

    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  onTreeStateChange(state: TreeState): void {
    this.treeState = state;
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  toggleSidebar(): void {
    if (this.isDesktop) return;

    this.isSidebarOpen = !this.isSidebarOpen;
    this.lockBodyScroll(this.isSidebarOpen);
  }

  closeSidebar(): void {
    if (!this.isSidebarOpen) {
      this.lockBodyScroll(false);
      return;
    }

    this.isSidebarOpen = false;
    this.lockBodyScroll(false);
  }

  startResizing(event: PointerEvent): void {
    if (!this.isDesktop) return;
    if (!this.showSidebar) return;
    event.preventDefault();

    const container = (event.currentTarget as HTMLElement).closest(
      '.main-content',
    ) as HTMLElement | null;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    this.containerLeft = rect.left;
    this.containerWidth = rect.width;

    this.isResizing = true;

    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
  }

  goToAdmin(): void {
    this.router.navigate(['/admin']);
  }

  private restoreSidebarWidth(): void {
    const stored = Number(localStorage.getItem(SIDEBAR_WIDTH_KEY));

    if (
      !Number.isNaN(stored) &&
      stored >= this.minSidebarPercent &&
      stored <= this.maxSidebarPercent
    ) {
      this.sidebarWidthPercent = stored;
    }
  }

  private persistSidebarWidth(): void {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, String(this.sidebarWidthPercent));
  }

  private lockBodyScroll(lock: boolean): void {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = lock ? 'hidden' : '';
  }

  private onPointerMove = (event: PointerEvent): void => {
    if (!this.isResizing || this.containerWidth === 0) return;

    const offsetX = event.clientX - this.containerLeft;
    let newPercent = (offsetX / this.containerWidth) * 100;

    if (newPercent < this.minSidebarPercent) {
      newPercent = this.minSidebarPercent;
    } else if (newPercent > this.maxSidebarPercent) {
      newPercent = this.maxSidebarPercent;
    }

    this.sidebarWidthPercent = newPercent;
    this.cdr.markForCheck();
  };

  private onPointerUp = (): void => {
    if (!this.isResizing) return;

    this.isResizing = false;
    this.persistSidebarWidth();

    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
  };

  private updateViewportMode(): void {
    const wasDesktop = this.isDesktop;
    this.isDesktop = window.innerWidth >= MOBILE_BREAKPOINT;

    if (!wasDesktop && this.isDesktop && this.isSidebarOpen) {
      this.closeSidebar();
    }

    if (wasDesktop && !this.isDesktop) {
      this.isSidebarOpen = false;
      this.isResizing = false;
      this.lockBodyScroll(false);
    }
  }
}
